import { brandString, brandToken, PLACEHOLDER } from '@parsina/framework/ui/lib/brand';

// ─────────────────────────────────────────────────────────────────────────────
// The brand in this app's OWN string tables (receptionStrings, the «صفحهٔ من»
// and onboarding tables, the ledger lines) — the same rules the kit's
// translator applies to its catalogues (@parsina/framework/ui/lib/brand.js,
// docs/rebranding/CONTRACT.md §4):
//
//   • the deployment's `strings` overlay for a key is asked FIRST;
//   • `{{brand}}`, `{{coin}}`, `{{coinEz}}` and `{{tagline}}` resolve in the
//     language of the table the text came from, `{{brand|بارسينا}}` keeping a
//     site's own spelling for as long as the deployment leaves the name unset;
//   • every other `{{name}}` is the caller's, and one it did not pass is left
//     exactly as written.
//
// With no brand configured every token resolves to the word the table spelled
// out before it had one, so each table renders what it rendered before, byte
// for byte (src/__tests__/brandParity.test.js).
// ─────────────────────────────────────────────────────────────────────────────

// Who falls back to whom when a table lacks a key (docs/locale/FRENCH.md D4): a
// reader of the Arabic script — Persian or Arabic — gets the Persian original, as
// Arabic always has; every other reader (French, German, and any language added later)
// gets English first, never a script they may not read.
const ARABIC_SCRIPT_READERS = new Set(['fa', 'ar']);
const fallbacksFor = (locale) => (ARABIC_SCRIPT_READERS.has(locale) ? ['fa'] : ['en', 'fa']);

/**
 * A table's text for `key` in `locale`: the brand's overlay, else the locale's
 * table, else the fallback's (English for a reader outside the Arabic script,
 * then Persian) — and the language the text is in, which is the one its tokens
 * resolve in. `value` is undefined when no table has the key.
 *
 * @param {{[locale: string]: object}} tables  flat key → text, per locale; `fa` is the last fallback
 */
export function lookupLocal(tables, locale, key) {
  const own = brandString(locale, key);
  if (own !== undefined) return { value: own, from: locale };
  if (tables[locale]?.[key] != null) return { value: tables[locale][key], from: locale };
  for (const id of fallbacksFor(locale)) {
    if (tables[id]?.[key] != null) return { value: tables[id][key], from: id };
  }
  return { value: tables.fa[key], from: 'fa' };
}

/**
 * `{{name}}` → the caller's value, else the brand's token for `locale`, else the
 * placeholder as written. One pass, so a caller's value that happens to contain
 * `{{brand}}` is inserted as it is and never resolved. Anything that is not a
 * string (a list) is returned untouched.
 */
export function fillTemplate(template, vars, locale) {
  if (typeof template !== 'string' || template.indexOf('{{') === -1) return template;
  return template.replace(PLACEHOLDER, (match, name, inline) => {
    const named = vars != null && Object.prototype.hasOwnProperty.call(vars, name);
    if (!named) {
      const token = brandToken(name, locale, inline);
      if (token !== undefined) return token;
    }
    const value = vars?.[name];
    return value == null ? match : String(value);
  });
}
